import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { ESkillType } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import editSkill from '@src/APIs/graphQl/Skill/editSkill';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import type SkillDetail from '@src/Objects/SkillDetail';
import type { AppState } from '@store/store';
import { Edit } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

interface EditSimpleSkillDialogProps {
    open: boolean;
    onClose: () => void;
    onSkillUpdated: (skill: SkillDetail) => void;
    skill: SkillDetail;
}

export default function EditSimpleSkillDialog({ open, onClose, onSkillUpdated, skill }: EditSimpleSkillDialogProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skills;
    const skillCategories = useSelector((state: AppState) => state.company.skillCategories);
    const offline = useSelector((state: AppState) => state.client.offline);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [categoryId, setCategoryId] = useState<number>(1);
    const [skillType, setSkillType] = useState<ESkillType>(ESkillType.HARD_SKILL);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open && skill) {
            setTitle(skill.title);
            setDescription(skill.description);
            setCategoryId(skill.categoryId);
            setSkillType(skill.skillType || ESkillType.HARD_SKILL);
            setError('');
        }
    }, [open, skill]);

    const handleSubmit = async () => {
        if (!title.trim()) {
            setError('Bitte geben Sie einen Skill-Namen ein');
            return;
        }

        if (!categoryId || categoryId <= 0) {
            setError('Keine gültige Skill-Kategorie gefunden. Bitte kontaktieren Sie den Administrator.');
            return;
        }

        if (!skillType) {
            setError('Bitte wählen Sie einen Skill-Typ aus');
            return;
        }

        setSaving(true);
        setError('');

        try {
            const updatedSkill = await editSkill(
                skill.id,
                title.trim(),
                description.trim() || 'Selbst erstellter Skill',
                categoryId,
                skill.otherNames || [],
                skillType,
                skill.productInfo?.productName || '',
                skill.productInfo?.manufacturer || '',
                skill.productInfo?.link || '',
                skill.acquisition || 'Selbststudium/Berufserfahrung',
                skill.acquisitionLink || '',
            );

            onSkillUpdated(updatedSkill);
            handleClose();
        } catch (err) {
            console.error('Skill update error:', err);
            const errorMessage =
                (err as any)?.message ||
                (err as Error)?.toString() ||
                'Unbekannter Fehler beim Aktualisieren des Skills';
            setError(`Fehler beim Aktualisieren des Skills: ${errorMessage}`);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setTitle('');
        setDescription('');
        setCategoryId(1);
        setSkillType(ESkillType.HARD_SKILL);
        setError('');
        onClose();
    };

    if (!open) return null;

    return (
        <Dialog
            id="EditSimpleSkillDialog"
            title="Skill bearbeiten"
            onClose={handleClose}
            footer={
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button onClick={handleClose} disabled={saving}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={saving || offline || !title.trim() || !categoryId || !skillType}
                    >
                        {saving ? 'Wird gespeichert...' : 'Speichern'}
                    </Button>
                </Box>
            }
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Edit size={20} />
                <Typography variant="h6">Skill bearbeiten</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                    required
                    id="skill-name-field"
                    label="Skill-Name"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    fullWidth
                    disabled={saving || offline}
                    error={!!error && !title.trim()}
                    InputLabelProps={{ shrink: true }}
                    helperText="Geben Sie einen eindeutigen Namen für den Skill ein"
                />

                <TextField
                    id="skill-description-field"
                    label="Beschreibung"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    disabled={saving || offline}
                    placeholder="Kurze Beschreibung des Skills (optional)"
                    InputLabelProps={{ shrink: true }}
                    helperText="Optionale Beschreibung zur Erläuterung des Skills"
                />

                <FormControl fullWidth disabled={saving || offline}>
                    <InputLabel>Skill-Typ</InputLabel>
                    <Select
                        value={skillType}
                        onChange={(e) => setSkillType(e.target.value as ESkillType)}
                        label="Skill-Typ"
                    >
                        <MenuItem value={ESkillType.HARD_SKILL}>Hard-Skill</MenuItem>
                        <MenuItem value={ESkillType.SOFT_SKILL}>Soft-Skill</MenuItem>
                        <MenuItem value={ESkillType.LANGUAGE_SKILL}>Sprach-Skill</MenuItem>
                    </Select>
                </FormControl>

                {error && (
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                )}

                {offline && (
                    <Typography color="warning" variant="body2">
                        Sie sind offline. Bitte prüfen Sie Ihre Internetverbindung.
                    </Typography>
                )}
            </Box>
        </Dialog>
    );
}
